import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { mindtimeWazuhComplianceApi } from 'src/api/mindtime/wazuhComplianceApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhComplianceControlItem,
  WazuhComplianceControlsQuery,
  WazuhComplianceSummaryData,
} from 'src/types/mindtime/wazuhCompliance';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';

export type ComplianceFilters = {
  framework?: string;
  status?: string;
  category?: string;
  wazuhAgentId?: string;
  organizationId?: string;
};

export type ComplianceSortParams = {
  column: string;
  sort: 'asc' | 'desc';
};

export type ComplianceState = {
  loading: boolean;
  summary: WazuhComplianceSummaryData | null;
  controls: WazuhPagedData<WazuhComplianceControlItem> | null;
};

type BuildComplianceQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  search?: string;
  filters?: ComplianceFilters;
  sortParams?: ComplianceSortParams | null;
  page?: number;
  pageSize?: number;
};

const emptyState = (): ComplianceState => ({
  loading: true,
  summary: null,
  controls: null,
});

const buildComplianceQuery = ({
  scope,
  profileOrgId,
  isReseller,
  search = '',
  filters = {},
  sortParams,
  page,
  pageSize,
}: BuildComplianceQueryInput): WazuhComplianceControlsQuery => {
  const { framework, status, category, wazuhAgentId, organizationId } = filters;
  const effectiveScope = buildEdrApiScope({
    scope,
    profileOrgId,
    isReseller,
    pageOrganizationId: organizationId || undefined,
  });

  return {
    ...effectiveScope,
    ...(typeof page === 'number' ? { page } : {}),
    ...(typeof pageSize === 'number' ? { pageSize } : {}),
    ...(search ? { search } : {}),
    ...(framework ? { framework } : {}),
    ...(status ? { status } : {}),
    ...(category ? { category } : {}),
    ...(wazuhAgentId ? { wazuhAgentId } : {}),
    ...(sortParams ? { column: sortParams.column, sort: sortParams.sort } : {}),
  };
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useComplianceExport = (
  search = '',
  filters: ComplianceFilters = {},
  sortParams: ComplianceSortParams | null = null
) => {
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    setExporting(true);
    mindtimeWazuhComplianceApi
      .export(buildComplianceQuery({ scope, profileOrgId, isReseller, search, filters, sortParams }))
      .then(({ blob, filename }) => {
        const name = filename?.trim() ? filename : 'compliance-controls-export.csv';
        triggerBlobDownload(blob, name);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'useComplianceExport');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => {
        setExporting(false);
      });
  }, [scope, profileOrgId, isReseller, search, filters, sortParams]);

  return { exporting, handleExport };
};

export const useComplianceData = (
  page = 0,
  pageSize = 50,
  search = '',
  filters: ComplianceFilters = {},
  sortParams: ComplianceSortParams | null = null
): ComplianceState => {
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<ComplianceState>(emptyState);

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        resellerId: scope.resellerId,
        startDate: scope.startDate,
        endDate: scope.endDate,
        isReseller,
      }),
    [scope.resellerId, scope.startDate, scope.endDate, isReseller]
  );

  const { framework, status, category, wazuhAgentId, organizationId } = filters;

  useEffect(() => {
    setState(emptyState());
  }, [scopeKey]);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    const listParams = buildComplianceQuery({
      scope,
      profileOrgId,
      isReseller,
      search,
      filters,
      sortParams,
      page,
      pageSize,
    });

    Promise.all([
      mindtimeWazuhComplianceApi.getSummary({
        resellerId: listParams.resellerId,
        organizationId: listParams.organizationId,
        framework: listParams.framework,
      }),
      mindtimeWazuhComplianceApi.getControls(listParams),
    ])
      .then(([summary, controls]) => {
        if (cancelled) return;
        setState({
          loading: false,
          summary,
          controls,
        });
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useComplianceData');
          setState((prev) => ({
            ...prev,
            loading: false,
            summary: null,
            controls: null,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    page,
    pageSize,
    search,
    framework,
    status,
    category,
    wazuhAgentId,
    organizationId,
    sortParams?.column,
    sortParams?.sort,
  ]);

  return state;
};
